/* istanbul ignore file */
import React, { useCallback } from 'react'
import _ from 'lodash'
import type { Node } from '../../../types'
import { AutomationTarget } from '../../../types'
import {
  type JsonViewerClassNames,
  isArrayOfObjects,
  ReadOnlyValue,
  ReadOnlyPrimitiveArray,
  ReadOnlyEmptyObject,
  PrimitiveArrayItems,
  NestedKeyLabel,
  IndexBadge,
  getBackgroundClass,
  cx,
} from '../shared/jsonRendering'

export type { JsonViewerClassNames }

interface JsonViewerProps extends AutomationTarget {
  data: Node
  emlId?: string
  classNames?: JsonViewerClassNames
}

interface JsonNodeProps {
  data: Node
  name?: string
  level?: number
  classNames?: JsonViewerClassNames
}

const ArrayNode = ({
  data,
  level = 0,
  classNames,
}: {
  data: Node[]
  level?: number
  classNames?: JsonViewerClassNames
}) => {
  return isArrayOfObjects(data) ? (
    <div className={cx('json-array')}>
      {data.map((item: Node, index: number) => (
        <div key={index}>
          <JsonNode
            data={item}
            name={`${index}`}
            level={level + 1}
            classNames={classNames}
          />
        </div>
      ))}
    </div>
  ) : (
    <PrimitiveArrayItems data={data} classNames={classNames} />
  )
}

const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  elmId = 'json_viewer',
  classNames,
}) => {
  return (
    <div className={cx('json-viewer', classNames?.viewer)} data-elm-id={elmId}>
      <JsonNode data={data} level={0} classNames={classNames} />
    </div>
  )
}

const JsonNode: React.FC<JsonNodeProps> = ({
  data,
  name,
  level = 0,
  classNames,
}) => {
  const renderValue = useCallback(
    (value: Node, keyName: string, level: number) => {
      const isObject = typeof value === 'object' && value !== null
      const isArray = Array.isArray(value)

      if (isObject || isArray) {
        // array of primitives
        if (isArray && !isArrayOfObjects(value as Node[])) {
          return (
            <ReadOnlyPrimitiveArray
              key={keyName}
              keyName={keyName}
              value={value as Node[]}
              classNames={classNames}
            />
          )
        }
        // object or array of objects
        return (
          <div className={cx('json-node', classNames?.value)} key={keyName}>
            {keyName && (
              <NestedKeyLabel keyName={keyName} classNames={classNames} />
            )}
            {/* don't bump level for arrays */}
            <JsonNode
              data={value}
              level={level + (isArray ? 0 : 1)}
              classNames={classNames}
            />
          </div>
        )
      }

      return (
        <ReadOnlyValue
          key={keyName}
          keyName={keyName}
          value={value}
          classNames={classNames}
        />
      )
    },
    [classNames]
  )

  const renderObjectNode = useCallback(
    (data: Record<string, Node>, level: number) => {
      return _.isEmpty(data) ? (
        <ReadOnlyEmptyObject classNames={classNames} />
      ) : (
        <>
          {Object.entries(data).map(([key, value]) =>
            renderValue(value, key, level)
          )}
        </>
      )
    },
    [classNames, renderValue]
  )

  const backgroundColorClass = getBackgroundClass(level)
  const isObject = typeof data === 'object' && data !== null
  const isArray = Array.isArray(data)

  if (isArray && isArrayOfObjects(data as Node[])) {
    return (
      <ArrayNode data={data as Node[]} level={level} classNames={classNames} />
    )
  }

  return (
    <div
      className={cx(
        'json-node-container',
        backgroundColorClass,
        classNames?.container
      )}
      data-level={level}
    >
      {name && <IndexBadge name={name} />}
      <>
        {/* eslint-disable-next-line no-nested-ternary */}
        {isObject ? (
          isArray ? (
            <ArrayNode
              data={data as Node[]}
              level={level}
              classNames={classNames}
            />
          ) : (
            renderObjectNode(data, level)
          )
        ) : (
          renderValue(data, name || '', level)
        )}
      </>
    </div>
  )
}

export default JsonViewer
